import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { HttpClient, useHttpClient } from '@bonusly/http-client';

import { EmployeeSpotlight } from '../schemas/spotlight';

/*
 * Shared utilities
 */

enum QueryKey {
	Spotlight = 'spotlight:employee-spotlight',
	Spotlights = 'spotlight:employee-spotlights',
}

const headers = {
	'Key-Inflection': 'camel',
};

/*
 * Path and query key builders
 */

export const spotlightQueryKey = (spotlightId: string) => [QueryKey.Spotlight, spotlightId];

export const spotlightsQueryKey = (userId: string) => [QueryKey.Spotlights, userId];

export const employeeSpotlightsPath = () => 'spotlight/employee_spotlights';
export const employeeSpotlightPath = (id: string) => `spotlight/employee_spotlights/${id}`;
export const employeeSpotlightSectionPath = (spotlightId: string, sectionId: string) => `spotlight/employee_spotlights/${spotlightId}/sections/${sectionId}`;
export const userSpotlightsListPath = (userId: string) => `spotlight/employee_spotlights?user_id=${userId}`;
export const employeeSpotlightEligibilityPath = (userId: string, startTime: string, endTime: string) =>
	`spotlight/employee_spotlights/eligibility?user_id=${userId}&start_time=${encodeURIComponent(startTime)}&end_time=${encodeURIComponent(endTime)}`;

/*
 * API Types
 */
type EmployeeSpotlightAPIData = {
	id: string;
	userId: string;
	companyId: string;
	generatedByUserId?: string | null;
	startTime: string;
	endTime: string;
	status: 'pending' | 'processing' | 'completed' | 'failed';
	totalReceivedCount?: number;
	totalGivenCount?: number;
	monthsAtCompany?: number;
	generationStartedAt?: string | null;
	generationCompletedAt?: string | null;
	generationError?: string | null;
	sections?: Array<{
		id?: string;
		sectionType?: string;
		intro?: string | null;
		title?: string | null;
		textHtml?: string | null;
		supportingEvidence?: Array<string | { bonusId?: string; source?: string; excerpt: string }>;
		strategy?: string;
		scores?: Record<string, unknown>;
	}>;
	createdAt: string;
	updatedAt: string;
	// Additional fields from full view
	data?: Record<string, unknown>;
	// Status helpers
	pending?: boolean;
	processing?: boolean;
	completed?: boolean;
	failed?: boolean;
	generationInProgress?: boolean;
	// Image and URL fields for spotlight attachment
	url: string;
	imageUrl?: string | null;
	imageHeight?: number | null;
	imageWidth?: number | null;
	thumbnailUrl?: string | null;
	thumbnailHeight?: number | null;
	thumbnailWidth?: number | null;
};

type CreateSpotlightAPIData = {
	user_id: string;
	start_time: string;
	end_time: string;
};

type SpotlightEligibilityAPIData = {
	eligible: boolean;
	receivedMessagesCount: number;
	minimumRequired: number;
};

/*
 * Query Hooks
 */

// Transform API section data to match schema
const transformSection = (section: NonNullable<EmployeeSpotlightAPIData['sections']>[0]) => ({
	id: section.id || '',
	intro: section.intro || '',
	title: section.title || '',
	textHtml: section.textHtml || '',
	supportingEvidence: section.supportingEvidence,
	strategy: section.strategy,
	scores: section.scores,
	type: section.sectionType || '',
});

// Transform API response to match schema
const transformSpotlightData = (data: EmployeeSpotlightAPIData) => ({
	id: data.id,
	userId: data.userId,
	companyId: data.companyId,
	generatedByUserId: data.generatedByUserId ?? null,
	startTime: data.startTime,
	endTime: data.endTime,
	status: data.status,
	totalReceivedCount: data.totalReceivedCount ?? 0,
	totalGivenCount: data.totalGivenCount ?? 0,
	monthsAtCompany: data.monthsAtCompany ?? 0,
	generationStartedAt: data.generationStartedAt ?? null,
	generationCompletedAt: data.generationCompletedAt ?? null,
	generationError: data.generationError ?? null,
	sections: (data.sections || []).map(transformSection),
	data: data.data,
	createdAt: data.createdAt,
	updatedAt: data.updatedAt,
	// Image and URL fields
	url: data.url,
	imageUrl: data.imageUrl,
	imageHeight: data.imageHeight,
	imageWidth: data.imageWidth,
	thumbnailUrl: data.thumbnailUrl,
	thumbnailHeight: data.thumbnailHeight,
	thumbnailWidth: data.thumbnailWidth,
});

const createSpotlightFetcher = (http: HttpClient, spotlightId: string) => async () => {
	if (!spotlightId) {
		throw new Error('Invalid spotlight ID');
	}

	const path = employeeSpotlightPath(spotlightId);
	const res = await http.get<EmployeeSpotlightAPIData>(path, {
		headers,
	});

	return EmployeeSpotlight.parse(transformSpotlightData(res.data));
};

export const useSpotlight = (spotlightId: string, options?: { enabled?: boolean }) => {
	const http = useHttpClient();
	const isEnabled = options?.enabled !== undefined ? options.enabled : !!spotlightId;

	return useQuery({
		queryKey: spotlightQueryKey(spotlightId),
		queryFn: createSpotlightFetcher(http, spotlightId),
		enabled: isEnabled,
	});
};

const createSpotlightsListFetcher = (http: HttpClient, userId: string) => async () => {
	const path = `${userSpotlightsListPath(userId)}`;
	const res = await http.get<EmployeeSpotlightAPIData[]>(path, {
		headers,
	});

	// Transform API response array to match schema
	return (res.data || []).map((data) => EmployeeSpotlight.parse(transformSpotlightData(data)));
};

export const useSpotlightsForUser = (userId: string) => {
	const http = useHttpClient();
	return useQuery({
		queryKey: [...spotlightsQueryKey(userId), 'list'],
		queryFn: createSpotlightsListFetcher(http, userId),
		enabled: !!userId,
	});
};

export const spotlightEligibilityQueryKey = (userId: string, startTime: string, endTime: string) => [
	QueryKey.Spotlights,
	userId,
	'eligibility',
	startTime,
	endTime,
];

export type SpotlightEligibilityQueryOptions = {
	enabled?: boolean;
	staleTime?: number;
};

export const useSpotlightEligibility = (userId: string, startTime: string, endTime: string, options?: SpotlightEligibilityQueryOptions) => {
	const http = useHttpClient();
	const isEnabled = options?.enabled !== undefined ? options.enabled : !!userId && !!startTime && !!endTime;

	return useQuery(
		spotlightEligibilityQueryKey(userId, startTime, endTime),
		async () => {
			const res = await http.get<SpotlightEligibilityAPIData>(employeeSpotlightEligibilityPath(userId, startTime, endTime), { headers });
			return res.data;
		},
		{
			...(options ?? {}),
			enabled: isEnabled,
		}
	);
};

/*
 * Mutation Hooks
 */
type CreateSpotlightOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (spotlight: EmployeeSpotlight) => void;
};

export const useCreateSpotlight = (options: CreateSpotlightOptions = {}) => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: CreateSpotlightAPIData) => {
			try {
				const res = await http.post<EmployeeSpotlightAPIData>(employeeSpotlightsPath(), data, {
					headers,
				});

				// Transform API response to match schema
				const spotlight = EmployeeSpotlight.parse(transformSpotlightData(res.data));

				// Update cache and ensure query stays active for polling
				// React Query's refetchInterval will handle polling globally
				queryClient.setQueryData(spotlightQueryKey(spotlight.id), spotlight);
				queryClient.invalidateQueries(spotlightsQueryKey(data.user_id));

				options.onSuccess?.(spotlight);
				return spotlight;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Update Spotlight Section mutation
 */
type UpdateSpotlightSectionData = {
	spotlightId: string;
	sectionId: string;
	textHtml: string;
};

type UpdateSpotlightSectionOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (spotlight: EmployeeSpotlight) => void;
};

export const useUpdateSpotlightSection = (options: UpdateSpotlightSectionOptions = {}) => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: UpdateSpotlightSectionData) => {
			try {
				const res = await http.patch<EmployeeSpotlightAPIData>(
					employeeSpotlightSectionPath(data.spotlightId, data.sectionId),
					{ text_html: data.textHtml },
					{ headers }
				);

				const spotlight = EmployeeSpotlight.parse(transformSpotlightData(res.data));
				queryClient.setQueryData(spotlightQueryKey(spotlight.id), spotlight);
				options.onSuccess?.(spotlight);
				return spotlight;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Delete Spotlight Section mutation
 */
type DeleteSpotlightSectionData = {
	spotlightId: string;
	sectionId: string;
};

type DeleteSpotlightSectionOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (spotlight: EmployeeSpotlight) => void;
};

export const useDeleteSpotlightSection = (options: DeleteSpotlightSectionOptions = {}) => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: DeleteSpotlightSectionData) => {
			try {
				const res = await http.delete<EmployeeSpotlightAPIData>(employeeSpotlightSectionPath(data.spotlightId, data.sectionId), { headers });

				const spotlight = EmployeeSpotlight.parse(transformSpotlightData(res.data));
				queryClient.setQueryData(spotlightQueryKey(spotlight.id), spotlight);
				options.onSuccess?.(spotlight);
				return spotlight;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};
